import { AppLayout, AuthLayout } from 'layouts';
import { getUserData } from 'lib/Helper/Auth';
import * as React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

export const LayoutSwitcher: React.FC = () => {
  const { pathname } = useLocation();
  const history = useHistory();
  // the build this tab loaded with, from version.json. the build writes a new one every deploy
  const loadedVersion = React.useRef<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = React.useState(false);

  // no more service worker, it kept serving old builds. src/service-worker.ts is the kill switch
  React.useEffect(() => {
    serviceWorkerRegistration.unregister();
  }, []);

  const checkVersion = React.useCallback(function checkVersion() {
    fetch(`${process.env.PUBLIC_URL}/version.json`, { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const version = data?.version;
        if (!version) return;
        if (loadedVersion.current === null) {
          loadedVersion.current = version;
          return;
        }
        if (version !== loadedVersion.current) {
          setUpdateAvailable(true);
        }
      })
      // no version.json locally, nothing to do
      .catch(() => undefined);
  }, []);

  // a page change is a safe moment to reload, nobody loses a half filled form
  React.useEffect(() => {
    if (updateAvailable) {
      window.location.reload();
      return;
    }
    checkVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // for tabs left open on one page, the banner shows up instead
  React.useEffect(() => {
    const timer = setInterval(checkVersion, 1000 * 60 * 30);
    return () => clearInterval(timer);
  }, [checkVersion]);

  const authUser = getUserData();
  const location = useLocation();
  const isAuth = location.pathname.indexOf('/auth') !== -1;

  const props = {
    updateAvailable,
    onUpdate: () => window.location.reload(),
  };

  React.useEffect(() => {
    if (authUser !== null && location.hash === '#logout') {
      history.replace('/dashboard');
    }
  }, [authUser, location, history]);

  return authUser !== null && !isAuth ? (
    <AppLayout {...props} />
  ) : (
    <AuthLayout {...props} />
  );
};
